import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React from 'react'

import { AutoComplete } from 'app/components/generic/FormFields'
import ModalForm from 'app/components/ModalForm'

const useStyles = makeStyles((theme) => ({
  field: { marginBottom: theme.spacing(2), minWidth: 220 }
}))

/**
 * Schema-driven create/edit modal for the apartment list pages.
 *
 * fields: [{ name, label, type, options?, multiline? }]
 *   type: 'text' | 'number' | 'date' | 'time' | 'select' | 'multiselect'
 *         | 'checkbox' | 'textarea'
 * Holds its own draft state seeded from `initialValues`; calls onSubmit(values).
 */
const ApartmentFormModal = ({
  open,
  title,
  fields,
  initialValues,
  onSubmit,
  onClose
}) => {
  const classes = useStyles()
  const [values, setValues] = React.useState(initialValues || {})

  // Seed the draft when the modal OPENS — and only then.
  //
  // This used to depend on `initialValues`, which every call site rebuilds on
  // each render: `emptyValues` is an object literal in the page body, and
  // `rowToForm(modal.row)` allocates inline. So ANY re-render of the page while
  // the modal was open reset the draft mid-edit and silently threw away what
  // had been entered.
  //
  // On Waitlist that lost the field you had just filled in: choosing a parent
  // fires `onSelect` -> `setChildren(...)` -> the page re-renders -> a new
  // `initialValues` identity -> reset -> the parent was wiped while the child
  // options it had just loaded stayed on screen. The POST then went out with
  // `parent: null`, which the API rejects with "Pick the parent first — a child
  // is chosen through them". Same trap for every field typed before the pick.
  //
  // The reset cannot simply be dropped: all four call sites keep this component
  // mounted and only toggle `open`, so without it the second row you opened
  // would still be showing the first one's draft.
  const wasOpen = React.useRef(false)
  React.useEffect(() => {
    if (open && !wasOpen.current) setValues(initialValues || {})
    wasOpen.current = open
  }, [open, initialValues])

  const setField = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }))

  const renderField = (field) => {
    const value = values[field.name] ?? ''
    switch (field.type) {
      case 'multiselect':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            size="small"
            className={classes.field}
            fullWidth
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              label={field.label}
              value={
                Array.isArray(values[field.name]) ? values[field.name] : []
              }
              onChange={(e) => setField(field.name, e.target.value)}
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selected.map((v) => {
                    const opt = (field.options || []).find(
                      (o) => (typeof o === 'object' ? o.value : o) === v
                    )
                    const lbl = opt && typeof opt === 'object' ? opt.label : v
                    return <Chip key={v} label={lbl} size="small" />
                  })}
                </div>
              )}
            >
              {(field.options || []).map((opt) => {
                const val = typeof opt === 'object' ? opt.value : opt
                const lbl = typeof opt === 'object' ? opt.label : opt
                return (
                  <MenuItem key={val} value={val}>
                    {lbl}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )
      case 'select':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            size="small"
            className={classes.field}
            fullWidth
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              label={field.label}
              value={value}
              onChange={(e) => {
                setField(field.name, e.target.value)
                // Lets a dependent field react — e.g. picking a child reloads
                // the batches they may trial in.
                if (field.onSelect) field.onSelect(e.target.value)
                ;(field.clears || []).forEach((name) => setField(name, ''))
              }}
            >
              {(field.options || []).map((opt) => {
                const val = typeof opt === 'object' ? opt.value : opt
                const lbl = typeof opt === 'object' ? opt.label : opt
                return (
                  <MenuItem key={val} value={val}>
                    {lbl}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )
      // A user picker: searchable by name or phone number, server-side.
      // `field.fetchValues(text)` -> [{ uuid, name }]. `field.onSelect(opt)`
      // lets a dependent field (e.g. child) react to the choice.
      //
      // AutoComplete is uncontrolled, so editing a row needs `field.initialValue`
      // ({ uuid, name }) to render what is already selected — setting the form
      // value alone would leave the box looking empty.
      case 'autocomplete':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            className={classes.field}
            fullWidth
          >
            <AutoComplete
              // Apartment-scoped opt-ins: 300ms throttle instead of the shared
              // 5000ms default (five seconds of an empty box reads as broken),
              // and fetch-on-open so clicking shows options instead of an empty
              // menu. Both default to the OLD behaviour everywhere else in Alfred.
              throttleMs={300}
              fetchOnOpen
              async
              name={field.name}
              label={field.label}
              multiple={false}
              initialValue={field.initialValue}
              // Without these the box is permanently empty: `handleSearch`
              // gates on `value.length >= minCharactersToSearch`, and an
              // undefined threshold makes that comparison always false, so no
              // fetch ever fires — not on mount, and not on typing either.
              minCharactersToSearch={0}
              searchWithEmptyValue
              fetchOnMount={field.fetchOnMount}
              fetchValues={field.fetchValues}
              onChange={(e, opt) => {
                setField(field.name, opt?.uuid || '')
                if (field.onSelect) field.onSelect(opt || null)
                ;(field.clears || []).forEach((name) => setField(name, ''))
              }}
              size="small"
            />
          </FormControl>
        )
      case 'checkbox':
        return (
          <FormControlLabel
            key={field.name}
            className={classes.field}
            control={
              <Checkbox
                checked={Boolean(values[field.name])}
                onChange={(e) => setField(field.name, e.target.checked)}
              />
            }
            label={field.label}
          />
        )
      case 'date':
      case 'datetime':
      case 'time':
        return (
          <TextField
            key={field.name}
            className={classes.field}
            type={
              // eslint-disable-next-line no-nested-ternary
              field.type === 'datetime'
                ? 'datetime-local'
                : field.type === 'time'
                ? 'time'
                : 'date'
            }
            label={field.label}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={value}
            onChange={(e) => setField(field.name, e.target.value)}
          />
        )
      default:
        return (
          <TextField
            key={field.name}
            className={classes.field}
            type={field.type === 'number' ? 'number' : 'text'}
            label={field.label}
            variant="outlined"
            size="small"
            fullWidth
            multiline={field.type === 'textarea'}
            minRows={field.type === 'textarea' ? 2 : undefined}
            value={value}
            onChange={(e) =>
              setField(
                field.name,
                field.type === 'number'
                  ? e.target.value === ''
                    ? ''
                    : Number(e.target.value)
                  : e.target.value
              )
            }
          />
        )
    }
  }

  return (
    <ModalForm
      isModalOpen={open}
      onModalClose={onClose}
      modalTitle={title}
      enableSave
      buttonLabel="Save"
      onSaveClick={() => onSubmit(values)}
      maxWidth="sm"
    >
      <div>{fields.map(renderField)}</div>
    </ModalForm>
  )
}

ApartmentFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ApartmentFormModal
